import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Badge, Button, Card, CardBody, Container, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { DATABASE_CLIENT_EDIT, DATABASE_CLIENT_FORM, DATABASE_VEHICULE_PAGE } from "../../routes/routeConstants";
import TableContainer from "../../components/Common/TableContainer";
import Breadcrumbs from "../../components/Common/Breadcrumb"
import { clientActions } from "./clientSlice";

function ClientPage() {
    const { t } = useTranslation('translation');
    const dispatch = useDispatch();
    const [record, setRecord] = useState({ open: false, data: null });
    const [importNotice, setImportNotice] = useState(null);
    const { clients, total, isFetching } = useSelector(state => state.client);

    const breadcrumbItems = [
        { title: t('database'), link: "#" },
        { title: t('client'), link: "#" },
    ];

    const navigate = useNavigate();

    useEffect(() => {
        load();
    }, []);

    const load =  () => {
        let query = {
            // criteria: { code: code, description: description },
            // page: page,
            size: 5,
        };
        dispatch(clientActions.search(query));
    }

    const downloadTemp = () => {
        let payload = {
            onSuccess: (response) => {
                var blob = new Blob([Uint8Array.from(atob(response), c => c.charCodeAt(0))], { type: "application/vnd.ms-excel" });
                var url = window.URL.createObjectURL(blob);
                var a = document.createElement('a');
                a.href = url;
                a.download = 'temp.xlsx';
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
            }
        }
        dispatch(clientActions.downloadTemp(payload));
    }

    const handleDelete = (cellProps) => {
        setRecord({ open: true, data: cellProps });
    }

    const confirmDelete = () => {
        setRecord({ ...record, open: false });
        let payload = {
            id: record.data.id,
            onSuccess: () => {
                load();
            },
            onError: (error) => {
                console.log(error);
            },
        };
        dispatch(clientActions.delete(payload));
    }

    const columns = useMemo(
        () => [
            {
                Header: t('clientf.type'),
                accessor: "type",
                disableFilters: true,
                filterable: false,
            },
            {
                Header: t('clientf.raison'),
                accessor: "raison",
                disableFilters: true,
                filterable: false,
            },
            {
                Header: "Véhicules",
                accessor: (client) => (
                    <div>
                        <Button
                            type="button"
                            size="sm"
                            color="light"
                            className="text-primary"
                            onClick={() => navigate(`${DATABASE_VEHICULE_PAGE}?clientId=${client.id}&proprietere=${encodeURIComponent(client.raison || "")}`)}
                        >
                            <i className="ri-car-line me-1" />
                            {client.vehiculeCount || 0} véhicule(s)
                        </Button>
                        {(client.vehicules || []).length > 0 && (
                            <div className="small text-muted mt-1 text-truncate" style={{ maxWidth: 320 }} title={client.vehicules.join(", ")}>
                                {client.vehicules.slice(0, 2).join(", ")}{client.vehicules.length > 2 ? ` +${client.vehicules.length - 2}` : ""}
                            </div>
                        )}
                    </div>
                ),
                disableFilters: true,
                filterable: false,
            },
            
            {
                Header: t('action'),
                accessor: (cellProps) => {
                    return (
                        <React.Fragment>
                            <Link to={DATABASE_CLIENT_EDIT.replace(":id", cellProps.id)} className="me-3 text-primary"><i className="mdi mdi-pencil font-size-18"></i></Link>
                            <Link onClick={() => handleDelete(cellProps)} className="text-danger"><i className="mdi mdi-trash-can font-size-18"></i></Link>
                        </React.Fragment>
                    )
                },
                disableFilters: true,
                filterable: false,
            },
        ],
        [navigate, t]
    );

    const uploadData = (event) => {
        const files = event.target.files;
        if (!files?.length) return;
        const formData = new FormData();
        formData.append("file", files[0]);
        let payload = {
            formData: formData,
            onSuccess: (response) => {
                setImportNotice({ color: "success", message: response?.message || "Import terminé avec succès. Les doublons existants ont été ignorés." });
                load();
            },
            onError: (error) => {
                setImportNotice({ color: "danger", message: error?.response?.data?.message || "Impossible d'importer le fichier Excel." });
            },
        };
        dispatch(clientActions.uploadData(payload));
        event.target.value = "";
    }

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Breadcrumbs title={t('client')} breadcrumbItems={breadcrumbItems} />
                    <Card>
                        <CardBody>
                            {importNotice && <Alert color={importNotice.color} toggle={() => setImportNotice(null)}>{importNotice.message}</Alert>}
                            <Alert color="light" className="mb-3 border text-dark">
                                <div className="d-flex gap-2 align-items-start">
                                    <i className="ri-file-excel-2-line fs-4 text-success" />
                                    <div>
                                        <strong className="text-dark">Format attendu pour l'import</strong>
                                        <div className="small mt-1 text-secondary">
                                            Fichier Excel <code>.xlsx</code> ou <code>.xls</code>, avec une première ligne contenant les colonnes
                                            <code className="mx-1">type</code> et <code>raison</code>. La colonne <code>type</code> accepte
                                            <code className="mx-1">PARTIC</code> (particulier) ou <code>ENTREP</code> (entreprise). Une ligne correspond à un client.
                                        </div>
                                    </div>
                                </div>
                            </Alert>

                            {!isFetching && !(clients || []).length && (
                                <div className="text-center border rounded py-5 mb-3 bg-light">
                                    <i className="ri-user-search-line display-6 text-muted" />
                                    <h5 className="mt-3 mb-1">Aucun client enregistré</h5>
                                    <p className="text-muted mb-0">Ajoutez un client, importez un fichier Excel ou créez un véhicule avec un propriétaire.</p>
                                </div>
                            )}

                            <TableContainer
                            canUpload={true}
                            withPages={true}
                                columns={columns || []}
                                data={clients ?? []}
                                isPagination={true}
                                isAddClientList={true}
                                canDownloadtemp={true}
                                customPageSizeOptions={true}
                                iscustomPageSize={false}
                                handleClientClick={() => navigate(DATABASE_CLIENT_FORM)}
                                handleDownloadTemp={() => downloadTemp()}
                                handleUpload={uploadData}
                                isBordered={false}
                                customPageSize={10}
                            />
                        </CardBody>
                    </Card>
                </Container>
            </div>
            <Modal
                isOpen={record.open}
                toggle={() => setRecord({ ...record, open: false })}
                backdrop="static"
            >
                <ModalHeader toggle={() => setRecord({ ...record, open: false })}>
                    {t('text.confirmation')}
                </ModalHeader>
                <ModalBody>
                    <p>
                        {t('text.msgDelete')}
                    </p>
                    <ModalFooter>
                        <Button type="button" color="light" onClick={() => setRecord({ ...record, open: false })}>{t('actions.close')}</Button>
                        <Button type="button" color="primary" onClick={confirmDelete}>{t('actions.confirm')}</Button>
                    </ModalFooter>
                </ModalBody>
            </Modal>
        </React.Fragment>
    )
}

export default ClientPage;
